import React, { useEffect, useMemo, useState } from 'react';
import Iframe from 'react-iframe';
import { connect } from 'react-redux';
import { Modal } from 'semantic-ui-react';
import { PaymentPageOpts, BY_SMS, BY_EMAIL } from '../../helpers/payments';
import { useForm } from '../../hooks/useForm';
import { CollectPaymentError, ExelotError, Parsers, UpdateError, Validators } from '../../helpers';
import { apiService } from '../../services';
import PayInspectionError from '../PayDuties/PayDutiesError';
import PayDetails from '../PayDuties/PayDetails';
import PayFormInspection from './PayFormInspection';
import { withTranslation } from 'react-i18next';

const defaultInspection = 'ministryOfCommunications';

const getInitalValues = () => ({
  email: '',
});

const PageLogActions = {
  TERMS_ACCEPTED: 'termsAccepted',
  TERM_UNSET: 'termsUnset',
  TERMS_WINDOW_OPENED: 'termsWindowOpened',
  PAY_SELECTED: 'paySelected',
  FORM_OPENED: 'formOpened'
};

const PayInspection = (props) => {
  const {
    getPayInspectionParcelAPI,
    updatePayInspectionParcelAPI,
    collectPayInspectionParcelAPI,
    addPageLogAPI,
    getFormURLAPI,
    history,
    match,
    t,
    i18n,
    location
  } = props;

  const [showForm, setShowForm] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [agreedTerms, setAgreedTerms] = useState(false);
  const [parcelId, setParcelId] = useState();
  const [inspection, setInspection] = useState();
  const [parcelData, setParcelData] = useState(null);
  const [errorMessage, setError] = useState(null);
  const [creditGuardUrl, setCreditGuardUrl] = useState();
  const [needAgreedTerms, setNeedAgreedTerms] = useState(true);
  const [paymentButton, setPaymentButton] = useState({ disabled: true });

  const query = useMemo(() => new URLSearchParams(location.search || ''));
  let linkBy = 'unknown';
  let commLinkID = '';
  if (query.get(BY_SMS)) {
    linkBy = 'sms';
    commLinkID = query.get(BY_SMS);
  } else if (query.get(BY_EMAIL)) {
    linkBy = 'email';
    commLinkID = query.get(BY_EMAIL);
  }

  const { fields, setValue, errors, setErrors, resetError } = useForm(getInitalValues);
  if (i18n.language !== 'he') {
    i18n.changeLanguage('he');
  }

  function handleCGPaymentCompletion(opts = {}) {
    console.log('Handling PayInspection CG Payment Completion callback');
    const { data } = opts;
    if (!data) {
      console.error('No data found');
      return;
    }

    const { status, page, res } = data;
    if (PaymentPageOpts[page] !== PaymentPageOpts.payInspection) {
      console.error(`Unexpected page ${page}`);
      return;
    }
    history.push(`/${page}/callback/${status}`, { ...res });
  }

  function toggleDetails() {
    setShowForm(s => !s);
  }

  function displayModal() {
    setShowModal(show => !show);
  }

  useEffect(() => {
    if (showModal) {
      addPageLog(PageLogActions.TERMS_WINDOW_OPENED);
    }
  }, [showModal])

  function areFieldsValid() {
    const errObj = {
      email: Validators.email(fields.email, `${t('emailErrorMessage')}`),
      buyerID: Validators.idNumber(fields.buyerID, `${t('idNumberErrorMessage')}`)
    };
    setErrors(errObj);

    if (Object.values(errObj).find(v => !!v)) {
      return false;
    }

    if (parcelData.buyer.isIDNeeded && !fields.buyerID) {
      return false;
    }

    return true;
  }

  function getUpdatedFields() {
    return Object.keys(fields).reduce((m, key) => {
      const value = fields[key];

      if (!value) {
        return m;
      }

      switch (key) {
        case 'email': {
          if (value !== parcelData.buyer.email) {
            m[key] = value;
          }
          break;
        }
        case 'buyerID': {
          m[key] = value;
          break;
        }
        default: {
          console.error(`Unexpected input field ${key}`);
        }
      }
      return m;
    }, {});
  }

  function updateParcel() {
    const updatedFields = getUpdatedFields();
    if (!Object.keys(updatedFields).length) {
      return Promise.resolve();
    }

    return apiService
      .callServer(
        updatePayInspectionParcelAPI,
        { ':id:': parcelId },
        `inspection=${inspection}`,
        JSON.stringify(updatedFields),
        null,
        true
      )
      .catch(() => {
        throw new UpdateError();
      });
  }

  function addPageLog(action, info) {
    if (!info) {
      info = {};
    }
    info.linkBy = linkBy;
    const pageLogData = {
      action,
      date: new Date(),
      info
    }

    return apiService
      .callServer(
        addPageLogAPI,
        { ':id:': parcelId },
        `inspection=${inspection}`,
        JSON.stringify(pageLogData),
        null,
        true
      )
      .catch((err) => {
        console.error(err);
      });
  }

  function collectPayment() {
    const baseCreditGuardCallbackUrl = `${window.location.protocol}//${window.location.hostname}${window.location.port ? `:${window.location.port}` : ''}/parcels/creditguard/process_payment?page=payInspection&status=:status:`;
    const creditGuardCallbackUrls = {
      success_url: baseCreditGuardCallbackUrl.replace(':status:', 'success'),
      error_url: baseCreditGuardCallbackUrl.replace(':status:', 'error'),
      cancel_url: baseCreditGuardCallbackUrl.replace(':status:', 'cancelled')
    };

    const totalSum = parseFloat(parcelData.duties.localCurrency.payments.total);

    return apiService
      .callServer(
        collectPayInspectionParcelAPI,
        { ':id:': parcelId },
        `inspection=${inspection}`,
        JSON.stringify({ ...creditGuardCallbackUrls, total: totalSum, commLinkID, paidViaComm: linkBy }),
        null,
        true
      )
      .then(res => {
        setCreditGuardUrl(res.url);
      })
      .catch(() => {
        throw new CollectPaymentError();
      });
  }

  const handleSubmit = () => {
    if (!areFieldsValid()) {
      return;
    }
    setSubmitted(true);

    addPageLog(PageLogActions.PAY_SELECTED, needAgreedTerms ? { 'termsAccepted': agreedTerms } : undefined);

    updateParcel()
      .then(collectPayment)
      .catch((err) => {
        if (err instanceof ExelotError) {
          setError(err.message);
          return;
        }

        // Should not happen
        console.error(err);

        // Using a default error message
        setError(new UpdateError().message);
      });
  };

  function handleChangeInput(ev) {
    const { value, name } = ev.target;
    if (errors[name]) resetError(name);
    setValue && setValue(Parsers.sanitizeString(value) || '', name);
  }

  function handleChangeCheckbox(_, data) {
    const { checked } = data;
    setAgreedTerms(checked);
    addPageLog(checked ? PageLogActions.TERMS_ACCEPTED : PageLogActions.TERM_UNSET);
  }

  function renderError() {
    if (!errorMessage) {
      return null;
    }

    return <PayInspectionError text={errorMessage} />;
  }

  function renderModal() {
    return (
      <Modal
        style={{ minHeight: '30%', position: 'relative' }}
        open={showModal}
        closeIcon={{ style: { top: '1.0535rem', right: '1rem' }, color: 'black', name: 'close' }}
        onClose={displayModal}
        size='small'>
        <Modal.Content>
          <div style={{ margin: '30px auto', textAlign: 'right', dir: 'rtl' }} className='display-linebreak'>
            <p>
              {t(parcelData.inspectionText.inspectionFeeTerms)}
            </p>
          </div>
        </Modal.Content>
      </Modal>
    );
  }

  function doPost(data, url) {
    const form = document.createElement('form');
    form.style.visibility = 'hidden';
    form.target = "_blank"
    form.method = 'POST';
    form.action = url;
    for (let field in data) {
      const input = document.createElement('input');
      input.name = field;
      input.value = data[field];
      form.appendChild(input)
    };
    document.body.appendChild(form);
    form.submit();
  }

  function openForm({ formID, filled }) {
    addPageLog(PageLogActions.FORM_OPENED, { formID, action: filled ? 'Update' : 'Fill' });
    apiService
      .callServer(getFormURLAPI, { ':formID:': formID, ':id:': parcelId })
      .then(res => {
        const preSet = {
          api_key: '1rY9T4Z90oJ-ewigtWXAaRzV6',
          api_user: 'lot18',
          ...res.body
        };
        doPost(preSet, res.url);
      })
      .catch(err => {
        setError(err);
      });
  }

  function onFocus() {
    getParcelData();
  }

  useEffect(() => {
    console.log('Adding PayInspection listener');
    window.addEventListener('message', handleCGPaymentCompletion, false);
    window.addEventListener("focus", onFocus);

    return () => {
      console.log('Removing PayInspection listener');
      window.removeEventListener('message', handleCGPaymentCompletion, false);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  useEffect(() => {
    if (!parcelData) {
      return;
    }

    const { hawb, buyer: { email } } = parcelData;

    if (email) {
      setValue(email, 'email');
    }

    updatePaymentBtn();

  }, [parcelData]);

  function updatePaymentBtn() {
    const { forms = [] } = parcelData;
    const emptyMandatoryForms = forms.find(f => f.mandatory === true && !f.filled);
    const isMandatoryFormsFilled = !emptyMandatoryForms || emptyMandatoryForms.length === 0

    const agreedTermsTitle = !agreedTerms && needAgreedTerms ? `${t('agreedTerms')}` : null;
    const mandatoryFormsTitle = !isMandatoryFormsFilled ? `${t('fillMandatoryForms')}` : null;
    let title = agreedTermsTitle || mandatoryFormsTitle ? t('please') : null;
    if (agreedTermsTitle) {
      title += agreedTermsTitle;
      title += mandatoryFormsTitle ? `${t('and')}` : '';
    }
    if (mandatoryFormsTitle) {
      title += `${mandatoryFormsTitle}`;
    }

    const disabled = (!agreedTerms && needAgreedTerms) || submitted || !fields.email || (parcelData.buyer.isIDNeeded && !fields.buyerID) || !isMandatoryFormsFilled;

    setPaymentButton({ disabled, title });
  }

  function getParcelData() {
    const id = Parsers.sanitizeString(match.params.id);
    if (!id) {
      setError(`${t('notFound')}`);
      return;
    }

    setParcelId(id);

    const inspection = match.params.inspection || defaultInspection;

    setInspection(inspection);

    apiService
      .callServer(getPayInspectionParcelAPI, { ':id:': id }, `inspection=${inspection}`, null, null, true)
      .then(res => {
        setParcelData(res);
        if (res && res.inspectionText && res.inspectionText.inspectionFeeTerms && res.inspectionText.inspectionFeeTerms.length) {
          setNeedAgreedTerms(true);
        } else {
          setNeedAgreedTerms(false);
        }
      }).catch((err) => {
        if (err === 'paymentTimeout') {
          setError(`${t('paymentTimeout')}`);
        } else {
          setError(`${t('notFound')}`);
        }
      });
  }

  useEffect(() => {
    getParcelData();

  }, [match]);

  useEffect(() => {
    if (!parcelData) {
      return;
    }
    updatePaymentBtn();
  }, [agreedTerms, needAgreedTerms, submitted, fields.email, fields.buyerID, parcelData?.buyer?.isIDNeeded, parcelData?.forms]);

  if (!parcelData) {
    return renderError();
  }

  if (creditGuardUrl) {
    return (
      <Iframe
        url={creditGuardUrl}
        className='cg-iframe'
      />
    );
  }

  const { hawb, isPaymentChargeless, isInspectionAlreadyPaid } = parcelData;

  return (
    <div className='payDuties'>
      {renderModal()}
      {renderError()}
      <div>
        {showForm
          ? (
            <PayFormInspection
              inspection={inspection}
              parcelData={parcelData}
              fields={fields}
              onChangeInput={handleChangeInput}
              onChangeCheckbok={handleChangeCheckbox}
              onClickButton={displayModal}
              openForm={openForm}
              errors={errors}
            />
          )
          : <PayDetails inspection={inspection} parcelData={parcelData} payType='inspection' />}


        {isInspectionAlreadyPaid &&
          <div className='actions animated fadeInUp'>
            <div className='section explanation bold'>
              {t('alreadyPaid', { hawb })}
            </div>
          </div>}
        {!isPaymentChargeless && 
          <div className='actions animated fadeInUp'>
            {!isInspectionAlreadyPaid &&
            <div className='section explanation bold'>
              {t('explination1')}<br />
              {t('explination2')}
            </div>
            }
            <button className='btn orange' id='paymentDetailsBtn' onClick={toggleDetails}>
              <i className='fa fa-info' aria-hidden='true' />
              {showForm ? `${t('paymentDetails')}` :
                `${t('deliveryDetails')}`}
            </button>
            {!isInspectionAlreadyPaid &&
              <button title={paymentButton.title} className='btn orange' onClick={handleSubmit} disabled={paymentButton.disabled}>
                <i className='fa fa-lock' aria-hidden='true' />
                {t('securePayment')}
              </button>}
          </div>}
      </div>
    </div >
  );
};

function mapStateToProps(state) {
  const {
    getPayInspectionParcel,
    updatePayInspectionParcel,
    collectPayInspectionParcel,
    addPageLog,
    getFormURL
  } = state.api;

  return {
    getPayInspectionParcelAPI: getPayInspectionParcel,
    updatePayInspectionParcelAPI: updatePayInspectionParcel,
    collectPayInspectionParcelAPI: collectPayInspectionParcel,
    addPageLogAPI: addPageLog,
    getFormURLAPI: getFormURL
  };
}

export default withTranslation('payInspection')(connect(mapStateToProps)(PayInspection));